import React, { useCallback, useEffect, useState } from 'react';
import { Check, ChevronDown, Loader2, Lock, Pencil, RefreshCw, Sparkles, Trash2, Wand2, X } from 'lucide-react';
import { apiService } from '../../services/api';

// Ce que Nexus sait de moi : son carnet sur moi, rangé par sections (qui je suis, mes priorités, ma façon de
// travailler, mes projets, avec qui, avec Nexus, ses savoir-faire pour moi, à retenir), tenu la nuit et au fil de nos
// échanges ; visible par moi seul. Je peux tout corriger ou retirer, et lui demander de relire maintenant.

const DEFAULT_MAX = 1200;
const POLL_MS = 5000;

const updatedLabel = (note) => {
  const when = note.updated_at ? new Date(`${note.updated_at}Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '';
  return `${note.updated_by === 'employee' ? 'Corrigé par toi' : 'Noté par Nexus'}${when ? ` · ${when}` : ''}`;
};

const NoteRow = ({ note, maxChars, onSave, onRemove, skill }) => {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (action) => {
    setBusy(true);
    try { await action(); } finally { setBusy(false); }
  };

  if (draft !== null) {
    return (
      <li className="space-y-2 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
        <p className="text-xs font-semibold text-slate-700">{note.title}</p>
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={5} maxLength={maxChars}
          aria-label={`Note « ${note.title} »`}
          className="w-full rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-800 focus:border-indigo-300 focus:outline-none" />
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={() => setDraft(null)} disabled={busy}
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-slate-500 hover:text-slate-800">
            <X className="h-3.5 w-3.5" /> Annuler
          </button>
          <button type="button" disabled={busy || !draft.trim()}
            onClick={() => run(async () => { await onSave(note, draft.trim()); setDraft(null); })}
            className="flex items-center gap-1 rounded-lg bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-40">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Enregistrer
          </button>
        </div>
      </li>
    );
  }
  return (
    <li className={`group rounded-xl border p-3 ${skill ? 'border-indigo-100 bg-gradient-to-br from-indigo-50/70 to-white' : 'border-slate-100 bg-white'}`}>
      <div className="flex items-start gap-2">
        {skill && <Wand2 className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />}
        <div className="min-w-0 flex-1">
          <p className={`text-xs font-semibold ${skill ? 'text-indigo-900' : 'text-slate-700'}`}>{note.title}</p>
          <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-slate-700">{note.content}</p>
          <p className="mt-1.5 text-[11px] text-slate-400">{updatedLabel(note)}</p>
        </div>
        <div className="flex shrink-0 gap-1 opacity-60 transition group-hover:opacity-100">
          <button type="button" onClick={() => setDraft(note.content)} title="Corriger"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button type="button" disabled={busy} title="Retirer"
            onClick={() => { if (window.confirm(`Retirer la note « ${note.title} » ? Nexus l'oubliera.`)) run(() => onRemove(note)); }}
            className="rounded-md p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </li>
  );
};

const Section = ({ section, notes, maxChars, onSave, onRemove }) => {
  const [open, setOpen] = useState(true);
  const skill = section.key === 'savoir-faire';
  return (
    <section>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 py-1 text-left">
        <ChevronDown className={`h-4 w-4 text-slate-400 transition ${open ? '' : '-rotate-90'}`} />
        <span className={`text-sm font-semibold ${skill ? 'text-indigo-900' : 'text-slate-800'}`}>{section.title}</span>
        <span className="rounded-full bg-slate-100 px-1.5 text-[11px] font-semibold text-slate-500">{notes.length}</span>
        <span className="hidden min-w-0 flex-1 truncate text-xs text-slate-400 sm:inline">{section.hint}</span>
      </button>
      {open && (
        <ul className="mt-1.5 grid gap-2 pl-6 lg:grid-cols-2">
          {notes.map((note) => <NoteRow key={note.key} note={note} maxChars={maxChars} onSave={onSave} onRemove={onRemove} skill={skill} />)}
        </ul>
      )}
    </section>
  );
};

const lastRead = (status) => {
  const at = status?.digest?.finished_at;
  if (!at) return 'Pas encore relu';
  return `Relu le ${new Date(`${at}Z`).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`;
};

const NexusNotesCard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => apiService.get('/me/nexus-notes')
    .then((next) => setData({ notes: next.notes || [], sections: next.sections || [], status: next.status || null, max: next.max_chars || DEFAULT_MAX }))
    .catch((err) => setError(err.message)), []);
  useEffect(() => { load(); }, [load]);
  const refreshing = Boolean(data?.status?.refreshing);
  useEffect(() => {
    if (!refreshing) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [refreshing, load]);

  const save = async (note, content) => {
    setError(null);
    try {
      await apiService.put(`/me/nexus-notes/${encodeURIComponent(note.key)}`, { title: note.title, content, section: note.section });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const remove = async (note) => {
    setError(null);
    try {
      await apiService.delete(`/me/nexus-notes/${encodeURIComponent(note.key)}`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const refresh = async () => {
    setError(null);
    try {
      await apiService.post('/me/nexus-notes/refresh', {});
      setData((d) => ({ ...d, status: { ...(d?.status || {}), refreshing: true } }));
    } catch (err) {
      setError(err.message);
    }
  };

  if (data === null && !error) {
    return <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Chargement…</p>;
  }
  const notes = data?.notes || [];
  const sections = (data?.sections?.length ? data.sections : [{ key: 'a-retenir', title: 'Notes', hint: '' }]);
  const known = new Set(sections.map((s) => s.key));
  const bySection = (key) => notes.filter((n) => (known.has(n.section) ? n.section : sections[sections.length - 1].key) === key);
  const empty = sections.filter((s) => !bySection(s.key).length);
  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 text-xs text-slate-500">
          <Sparkles className="h-3.5 w-3.5 text-indigo-400" /> {notes.length} note{notes.length > 1 ? 's' : ''} · {lastRead(data?.status)}
        </span>
        {data?.sections?.length > 0 && (
          <button type="button" onClick={refresh} disabled={refreshing}
            className="ml-auto flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Nexus relit…' : 'Relire maintenant'}
          </button>
        )}
      </div>
      {notes.length ? (
        <div className="space-y-3">
          {sections.filter((s) => bySection(s.key).length).map((section) => (
            <Section key={section.key} section={section} notes={bySection(section.key)} maxChars={data?.max || DEFAULT_MAX} onSave={save} onRemove={remove} />
          ))}
          {empty.length > 0 && data?.sections?.length > 0 && (
            <p className="text-xs text-slate-400">Encore vide : {empty.map((s) => s.title.toLowerCase()).join(', ')}.</p>
          )}
        </div>
      ) : (
        <p className="rounded-xl border border-dashed border-slate-200 p-3 text-center text-slate-500">
          Nexus n'a encore rien noté sur toi. Il complète ce carnet chaque nuit et au fil de vos échanges.
        </p>
      )}
      <p className="flex items-start gap-1.5 text-xs text-slate-400">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Visible par toi seul. Nexus s'en sert pour te répondre, suivre tes façons de faire et préparer ta journée ;
        corrige ou retire ce qui n'est pas juste : ta version fait foi.
      </p>
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
};

export default NexusNotesCard;
